import { createAction, createGetter, createMutation, mutate, on, select, StoreFeature } from "@softline/core";
import { inject } from "@angular/core";
import { Template } from "./template";
import { lastValueFrom } from "rxjs";
import { QueryHistoryService } from "./services/query-history.service";

export interface LoadQueryHistoryParameters {
  group?: string;
}

export interface State<T> {
  queryHistory: Template<T>[]
}

export class Store<T> {

  mutations = {
    queryHistory: {
      add: createMutation<State<T>, Template<T>>('add query history'),
      addMany: createMutation<State<T>, Template<T>[]>('addMany query history'),
      remove: createMutation<State<T>, Template<T>>('remove query history'),
      clear: createMutation<State<T>>('clear query history'),
    },
  };

  actions = {
    queryHistory: {
      loadMany: createAction<State<T>, LoadQueryHistoryParameters, Template<T>[]>(
        'load query history'
      ),
      create: createAction<State<T>, Template<T>, Template<T>>(
        'create query history'
      ),
      delete: createAction<State<T>, Template<T>, Template<T>>(
        'delete query history'
      ),
    },
  };

  getters = {
    queryHistory: {
      templates: createGetter<State<T>, Template<T>[], string | undefined>('query history templates'),
      template: createGetter<State<T>, Template<T>, string | number>('query history template'),
    },
  };

  feature: StoreFeature<State<T>> = {
    initialState: {
      queryHistory: [],
    },
    mutations: [
      mutate(this.mutations.queryHistory.add, ({ state, params }) => {
        const queryHistory = state.queryHistory.filter(o => o.id !== params.id);
        queryHistory.push(params)
        return { ...state, queryHistory };
      }),
      mutate(this.mutations.queryHistory.addMany, ({ state, params }) => {
        const queryHistory = [...state.queryHistory]
        for(const template of params) {
          const index = queryHistory.findIndex(o => o.id === template.id)
          if(index > -1)
            queryHistory.splice(index, 1, template);
          else
            queryHistory.push(template);
        }
        return { ...state, queryHistory };
      }),
      mutate(this.mutations.queryHistory.remove, ({ state, params }) => {
        const index = state.queryHistory.indexOf(params);
        if (index === -1)
          throw new Error(
            `QueryHistoryStore: Can not process mutation 'remove'. Can not find item '${JSON.stringify(
              params
            )}'`
          );

        const queryHistory = state.queryHistory.filter((o) => o !== params);
        return { ...state, queryHistory };
      }),
      mutate(this.mutations.queryHistory.clear, ({ state, params }) => ({
        ...state,
        items: [],
      })),
    ],
    actions: [
      on(
        this.actions.queryHistory.loadMany,
        async ({ featureName, params, commit }) => {
          const service = inject(QueryHistoryService);
          const templates = await lastValueFrom(service.loadMany(params?.group));
          commit(
            featureName,
            this.mutations.queryHistory.addMany,
            templates
          );
          return templates;
        }
      ),
      on(
        this.actions.queryHistory.create,
        async ({ featureName, params, commit }) => {
          const service = inject(QueryHistoryService);
          const template = await lastValueFrom(service.create(params));
          commit(
            featureName,
            this.mutations.queryHistory.add,
            template
          );
          return template;
        }
      ),
      on(
        this.actions.queryHistory.delete,
        async ({ featureName, params, commit }) => {
          const service = inject(QueryHistoryService);
          const template = await lastValueFrom(service.delete(params));
          commit(
            featureName,
            this.mutations.queryHistory.remove,
            template
          );
          return template;
        }
      ),
    ],
    getters: [
      select(this.getters.queryHistory.template, ({ state, params }) => state.queryHistory.find(o => o.id === params)),
      select(this.getters.queryHistory.templates, ({ state, params }) =>
        state.queryHistory.filter(o => !params || o.group === params)
      ),
    ],
  };
}

export function create<T>(): Store<T> {
  return new Store<T>();
}

const instance = create();
export const mutations = instance.mutations;
export const getters = instance.getters;
export const actions = instance.actions;
export const feature = instance.feature;
